/**
 * Coverage B and Coverage C sub-limits (OR-1455).
 *
 * The Policy Form's declarations page prints six figures Orion never
 * captured, so today the UW fills them in by hand after generating the
 * document. These two cards close that gap; they sit under Policy Terms
 * and follow its shape exactly — `MoneySelect` with the standard amount
 * preselected, Custom for anything else.
 *
 * ⚠️ Andressa's mock labels BOTH cards "Coverage B — General Liability",
 * but the evacuation figures are Coverage C in the backend
 * (`CoverageCAmounts` in `services/v2/exports/policy.py`). Corrected here
 * — flagged to design.
 *
 * The values persist locally (see `api/dec-fields-local`) until
 * orion-backend PR #326 deploys and the API returns/stores them itself.
 */
import { Shield01 } from "@untitledui/icons";
import { FormGroup } from "../../../../components/form-group";
import { MoneySelect } from "../../../../components/money-select";
import {
    EVACUATION_PRESETS,
    FIRE_DAMAGE_AGGREGATE_PRESETS,
    GL_EACH_CLAIM_DEDUCTIBLE_PRESETS,
    MEDICAL_EXPENSE_PRESETS,
    clampMoneyToWire,
} from "../premium-selection-types";
import type { PremiumSelectionInputs } from "../../../../domain/pricing";
import { CardRows, FieldRow } from "../components/field-row";
import { useBlurFlash } from "../components/use-blur-flash";
import type { InputKey } from "../use-premium-selection";

interface LimitDef {
    key: InputKey;
    label: string;
    presets: number[];
    dataAutotest: string;
}

const COVERAGE_B_LIMITS: LimitDef[] = [
    // The dec prints a "Fire Damage Per Claim Limit of Liability" row
    // (`CoverageBAmounts.fire_per_claim`) and the old Limits of Liability
    // modal captured it, but OR-1455's field list skipped it — so it has no
    // backend column yet and no default anyone wrote down. Ships empty and
    // forward-compatible; flagged to backend.
    {
        key: "fireDamagePerClaimLimit",
        label: "Fire Damage Per Claim Limit",
        presets: FIRE_DAMAGE_AGGREGATE_PRESETS,
        dataAutotest: "premium-selection-coverage-b-fire-damage-per-claim-limit-row",
    },
    {
        key: "fireDamageAggregateLimit",
        label: "Fire Damage Aggregate Limit",
        presets: FIRE_DAMAGE_AGGREGATE_PRESETS,
        dataAutotest: "premium-selection-coverage-b-fire-damage-aggregate-limit-row",
    },
    {
        key: "glEachClaimDeductible",
        label: "GL Each Claim Deductible",
        presets: GL_EACH_CLAIM_DEDUCTIBLE_PRESETS,
        dataAutotest: "premium-selection-coverage-b-gl-each-claim-deductible-row",
    },
    {
        key: "medicalExpenseLimit",
        label: "Medical Expense Limit",
        presets: MEDICAL_EXPENSE_PRESETS,
        dataAutotest: "premium-selection-coverage-b-medical-expense-limit-row",
    },
];

const COVERAGE_C_LIMITS: LimitDef[] = [
    {
        key: "evacuationLimit",
        label: "Evacuation Limit (per evacuation)",
        presets: EVACUATION_PRESETS,
        dataAutotest: "premium-selection-coverage-c-evacuation-limit-row",
    },
    {
        key: "evacuationAggregateLimit",
        label: "Evacuation Aggregate Limit",
        presets: EVACUATION_PRESETS,
        dataAutotest: "premium-selection-coverage-c-evacuation-aggregate-limit-row",
    },
    {
        key: "evacuationDeductible",
        label: "Evacuation Deductible (per claim)",
        presets: EVACUATION_PRESETS,
        dataAutotest: "premium-selection-coverage-c-evacuation-deductible-row",
    },
];

interface CoverageLimitsCardProps {
    inputs: PremiumSelectionInputs;
    setInput: (key: InputKey, value: string) => void;
}

export function CoverageBCard({ inputs, setInput }: CoverageLimitsCardProps) {
    return (
        <LimitsCard
            title="Coverage B — General Liability"
            limits={COVERAGE_B_LIMITS}
            inputs={inputs}
            setInput={setInput}
            dataAutotest="premium-selection-coverage-b-card"
        />
    );
}

export function CoverageCCard({ inputs, setInput }: CoverageLimitsCardProps) {
    return (
        <LimitsCard
            title="Coverage C — Evacuation Expense"
            limits={COVERAGE_C_LIMITS}
            inputs={inputs}
            setInput={setInput}
            dataAutotest="premium-selection-coverage-c-card"
        />
    );
}

function LimitsCard({
    title,
    limits,
    inputs,
    setInput,
    dataAutotest,
}: CoverageLimitsCardProps & { title: string; limits: LimitDef[]; dataAutotest: string }) {
    return (
        <FormGroup title={title} icon={Shield01} data-autotest={dataAutotest}>
            <CardRows>
                {limits.map(({ key, label, presets, dataAutotest }) => (
                    <LimitRow
                        key={key}
                        inputKey={key}
                        label={label}
                        presets={presets}
                        value={inputs[key]}
                        setInput={setInput}
                        dataAutotest={dataAutotest}
                    />
                ))}
            </CardRows>
        </FormGroup>
    );
}

function LimitRow({
    inputKey,
    label,
    presets,
    value,
    setInput,
    dataAutotest,
}: {
    inputKey: InputKey;
    label: string;
    presets: number[];
    value: string;
    setInput: (key: InputKey, value: string) => void;
    dataAutotest: string;
}) {
    const { flashKey, flash } = useBlurFlash();
    return (
        <FieldRow label={label} flashKey={flashKey} controlClassName="w-44" data-autotest={dataAutotest}>
            <MoneySelect
                aria-label={label}
                value={value}
                presets={presets}
                onChange={(next) => {
                    // Keep the PUT inside the wire's Numeric(14,2) range.
                    setInput(inputKey, clampMoneyToWire(next));
                    flash();
                }}
            />
        </FieldRow>
    );
}
